"use client";

import React, { useRef } from "react";
import {
  Box,
  Typography,
  Grid2,
  styled,
  Paper,
  Stack,
  useMediaQuery,
  useTheme,
  Skeleton,
} from "@mui/material";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Project } from "../projects/data";
import { useProjects } from "../../hooks/useProjects";
import { Link } from "react-router-dom";

// interface AppData {
//   type: string;
//   name: string;
//   description: string;
//   icon: string;
//   image: string;
// }

// const apps: AppData[] = [
//   {
//     type: "iOS App",
//     name: "Glide",
//     description: "Navigate the world of web technology",
//     icon: "https://framerusercontent.com/images/AkMSdefy4w8t3vgUB4zSeSuyyFQ.png",
//     image:
//       "https://framerusercontent.com/images/gGr7r5BWikwvibWmbgQH3Gsc81M.png?scale-down-to=512",
//   },
//   {
//     type: "iOS App",
//     name: "Glide",
//     description: "Navigate the world of web technology",
//     icon: "https://framerusercontent.com/images/AkMSdefy4w8t3vgUB4zSeSuyyFQ.png",
//     image:
//       "https://framerusercontent.com/images/gGr7r5BWikwvibWmbgQH3Gsc81M.png?scale-down-to=512",
//   },
//   {
//     type: "iOS App",
//     name: "Glide",
//     description: "Navigate the world of web technology",
//     icon: "https://framerusercontent.com/images/AkMSdefy4w8t3vgUB4zSeSuyyFQ.png",
//     image:
//       "https://framerusercontent.com/images/gGr7r5BWikwvibWmbgQH3Gsc81M.png?scale-down-to=512",
//   },
//   {
//     type: "iOS App",
//     name: "Glide",
//     description: "Navigate the world of web technology",
//     icon: "https://framerusercontent.com/images/AkMSdefy4w8t3vgUB4zSeSuyyFQ.png",
//     image:
//       "https://framerusercontent.com/images/gGr7r5BWikwvibWmbgQH3Gsc81M.png?scale-down-to=512",
//   },
//   {
//     type: "iOS App",
//     name: "Glide",
//     description: "Navigate the world of web technology",
//     icon: "https://framerusercontent.com/images/AkMSdefy4w8t3vgUB4zSeSuyyFQ.png",
//     image:
//       "https://framerusercontent.com/images/gGr7r5BWikwvibWmbgQH3Gsc81M.png?scale-down-to=512",
//   },
//   {
//     type: "iOS App",
//     name: "Glide",
//     description: "Navigate the world of web technology",
//     icon: "https://framerusercontent.com/images/AkMSdefy4w8t3vgUB4zSeSuyyFQ.png",
//     image:
//       "https://framerusercontent.com/images/gGr7r5BWikwvibWmbgQH3Gsc81M.png?scale-down-to=512",
//   },
// ];

const ImageWrapper = styled(motion.div)<{ imgsrc: string }>(({ imgsrc }) => ({
  position: "relative",
  backgroundSize: "cover",
  backgroundImage: `url(${imgsrc})`,
  borderRadius: "16px",
  "&::before": {
    content: '""',
    width: "100%",
    height: "100%",
    position: "absolute",
    borderRadius: "inherit",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "inherit",
    filter: "blur(60px)",
    transform: "scale(1)",
    opacity: 0,
    transition: "opacity 0.4s ease",
  },
}));

const AppImage = styled(motion.img)({
  width: "100%",
  height: "auto",
  borderRadius: "16px",
  position: "relative",
});

const AppIcon = styled(motion.img)({
  width: "44px",
  height: "44px",
  borderRadius: "4px",
  marginLeft: "8px",
  filter: "grayscale(1) contrast(1.1)",
  transition: ".4s ease",
});

interface AppCardProps {
  project: Project;
  index: number;
}

const AppCard: React.FC<AppCardProps> = ({ project, index }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const cardRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "end start"],
  });

  const springConfig = { stiffness: 300, damping: 30, restDelta: 0.001 };

  const y = useSpring(
    useTransform(scrollYProgress, [0, 1], [100, -100]),
    springConfig
  );

  const opacity = useSpring(
    useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]),
    springConfig
  );

  const scale = useSpring(
    useTransform(scrollYProgress, [0, 0.5, 0.8, 1], [0.8, 1, 1, 0.8]),
    springConfig
  );

  return (
    <Grid2 size={{ xs: 12, md: 6 }}>
      <Link to={`/projects/${project.slug}`} key={`${project.name}-${index}`}>
      <motion.div
        ref={cardRef}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.2 }}
      >
        <Box
          sx={{
            cursor: "pointer",
            position: "relative",
            mb: { xs: 8, md: 4 },
            [theme.breakpoints.only("xs")]: {
              ".IconEffect": {
                transform: "translateY(0px)",
                filter: "grayscale(0)",
              },
              ".imgDynamicShadow::before": {
                opacity: 1,
              },
            },
            [theme.breakpoints.up("sm")]: {
              "&:hover": {
                ".IconEffect": {
                  transform: "translateY(-5px)",
                  filter: "grayscale(0)",
                },
                ".imgDynamicShadow::before": {
                  opacity: 1,
                },
              },
            },
          }}
        >
          <motion.div style={{ y, opacity, scale }}>
            <ImageWrapper imgsrc={project.image[0]} className="imgDynamicShadow">
              <AppImage src={project.image[0]} alt={`${project.name} preview`} />
            </ImageWrapper>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.2 + 0.2 }}
            style={{
              position: "absolute",
              width: "100%",
              right: 0,
              left: 0,
              bottom: isMobile ? -75 : -45,
              backdropFilter: "blur(8px)",
              zIndex: 1,
            }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 2.5,
                borderRadius: 5,
                background: "none",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-end"
                spacing={2}
              >
                <Box>
                  <Paper sx={{ width: "auto", display: "inline-block", mb: 1 }}>
                    <Typography
                      variant="overline"
                      sx={{ p: 0.8, textTransform: "capitalize" }}
                    >
                      {project.category}
                    </Typography>
                  </Paper>
                  <Typography
                    variant="h4"
                    component="h4"
                    sx={{ fontWeight: 600, mt: 1 }}
                    gutterBottom
                  >
                    {project.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontWeight: 300,
                    }}
                    dangerouslySetInnerHTML={{ __html: project.overview ?? "" }}
                  />
                </Box>
                <AppIcon
                  src={project.logo}
                  alt={`${project.name} icon`}
                  className="IconEffect"
                />
              </Stack>
            </Paper>
          </motion.div>
        </Box>
      </motion.div>
      </Link>
    </Grid2>
  );
};

const SkeletonCard = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  return (
    <Grid2 size={{ xs: 12, md: 6 }}>
      <Box sx={{ mb: { xs: 8, md: 4 }, position: 'relative' }}>
        <Skeleton
          variant="rectangular"
          width="80%"
          height={300}
          animation="wave"
          sx={{ borderRadius: '16px', margin: "0 auto" }}
        />
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            right: 0,
            left: 0,
            bottom: isMobile ? -75 : -45,
            zIndex: 1,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 2.5,
              borderRadius: 5,
              backdropFilter: 'blur(8px)',
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-end"
              spacing={2}
            >
              <Box sx={{ width: '100%' }}>
                <Skeleton variant="text" height={24} width="20%" sx={{ mb: 1 }} />
                <Skeleton variant="text" height={32} width="40%" sx={{ mb: 1 }} />
                <Skeleton variant="text" height={20} width="80%" />
              </Box>
              <Skeleton
                variant="rectangular"
                width={50}
                height={50}
                sx={{ borderRadius: '16px'}}
              />
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Grid2>
  )
}

export default function Projects() {
  const { allProjects, loading, error } = useProjects(undefined)

  if (error) {
    return (
      <Box id="projects" sx={{ py: 10 }}>
        <Typography variant="h4" color="error" textAlign="center">
          Error loading projects. Please try again later.
        </Typography>
      </Box>
    )
  }

  return (
    <Box id="projects">
      <Grid2 container spacing={4} py={10}>
        {loading
          ? Array.from(new Array(6)).map((_, index) => (
              <SkeletonCard key={index} />
            ))
          : allProjects.map((project, index) => (
              <AppCard key={project.slug} project={project} index={index} />
            ))}
      </Grid2>
    </Box>
  )
}
